/**
 * RECORD-HISTORY-01 (Seth 2026-08-26: "do the history tab on the record") —
 * the "who did what to THIS record" feed, right on the record. Fed by the
 * same pegasus_audit spine as Settings → Audit Log (a database trigger
 * captures EVERY change to every Pegasus table — nothing bypasses it), just
 * filtered to the record you're looking at: the job, its visits, whatever
 * the page passes in. "Who moved this call?" answered without leaving it.
 *
 * Visibility rides the audit RLS (audit.view — admins/managers): anyone else
 * simply never sees the section.
 */
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useStaffNames, staffFirst } from "@/lib/staffNames";

interface AuditRow {
  id: number;
  at: string;
  actor: string | null;
  actor_uid: string | null;
  action: "insert" | "update" | "delete";
  entity: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changed_cols: string[] | null;
}

export interface HistoryTarget { entity: string; id: string }

/** Table → the phrase a human says. Falls back to a cleaned table name. */
const ENTITY_PHRASE: Record<string, string> = {
  pegasus_job: "the job",
  pegasus_dispatch: "a visit",
  pegasus_post_install: "post-install",
  pegasus_job_equipment: "equipment",
  pegasus_job_labor: "labor",
  pegasus_job_material: "materials",
  pegasus_job_invoice: "the invoice",
  pegasus_job_invoice_line: "an invoice line",
  pegasus_permit: "a permit",
  pegasus_permit_inspection: "a permit inspection",
  pegasus_builder: "builder settings",
  pegasus_quote: "the proposal",
  pegasus_file: "a file",
  pegasus_payment: "a payment",
};
const phrase = (entity: string) => ENTITY_PHRASE[entity] ?? entity.replace(/^pegasus_/, "").replace(/_/g, " ");

const NOISE_COLS = new Set(["updated_at", "updated_by", "created_at", "created_by", "id"]);

const fmtVal = (v: unknown): string => {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return s.length > 36 ? `${s.slice(0, 36)}…` : s;
};

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function RecordHistory({ targets, title = "History" }: { targets: HistoryTarget[]; title?: string }) {
  const canView = useCan("audit.view");
  const staff = useStaffNames().data;
  const key = targets.map((t) => `${t.entity}:${t.id}`).sort().join("|");
  const { data: rows } = useQuery({
    queryKey: ["record_history", key],
    enabled: canView && targets.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AuditRow[]> => {
      const ors = targets.map((t) => `and(entity.eq.${t.entity},entity_id.eq.${t.id})`).join(",");
      const { data } = await supabase.from("pegasus_audit")
        .select("id, at, actor, actor_uid, action, entity, entity_id, before, after, changed_cols")
        .or(ors).order("at", { ascending: false }).limit(50);
      return (data ?? []) as AuditRow[];
    },
  });

  if (!canView) return null;
  const list = rows ?? [];

  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-2.5 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider text-slate-500">
        <History className="h-4 w-4" /> {title}
        <span className="ml-auto text-[11px] font-medium normal-case tracking-normal text-slate-400">
          every change, captured automatically — full log in Settings → Audit Log
        </span>
      </h3>
      {list.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">No changes recorded on this record yet.</p>
      ) : (
        <ul className="space-y-1">
          {list.map((r) => {
            const changed = (r.changed_cols ?? []).filter((c) => !NOISE_COLS.has(c)).slice(0, 3);
            return (
              <li key={r.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
                <span className="font-bold text-slate-800">{staffFirst(staff, r.actor, r.actor_uid)}</span>
                <span className="text-slate-600">
                  {r.action === "insert" ? `added ${phrase(r.entity)}`
                    : r.action === "delete" ? `removed ${phrase(r.entity)}`
                    : `changed ${phrase(r.entity)}`}
                </span>
                {r.action === "update" && changed.length > 0 && (
                  <span className="min-w-0 text-[13px] text-slate-500">
                    {changed.map((c, i) => (
                      <span key={c}>
                        {i > 0 && " · "}
                        <span className="font-medium text-slate-600">{c.replace(/_/g, " ")}</span>
                        {": "}
                        <span className="text-slate-400 line-through decoration-slate-300">{fmtVal(r.before?.[c])}</span>
                        {" → "}
                        <span className="font-semibold text-slate-700">{fmtVal(r.after?.[c])}</span>
                      </span>
                    ))}
                  </span>
                )}
                <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400">{when(r.at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
